import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import {
  sendCustomAppAction,
  useDesktopApp,
} from '@/modules/desktop/DesktopAppProvider'
import { noop } from '@/utils/misc.ts'

import { getOs } from './os'

export const themes = ['system', 'light', 'dark'] as const
export type Theme = (typeof themes)[number]

export const ThemeContext = React.createContext<{
  theme: Theme
  styleTheme: Omit<Theme, 'system'>
  changeTheme: (theme: Theme) => void
  blackFridayBannerOpen: boolean
  setBlackFridayBannerOpen: (open: boolean) => void
}>({
  theme: 'system',
  styleTheme: 'light',
  changeTheme: noop,
  blackFridayBannerOpen: false,
  setBlackFridayBannerOpen: noop,
})

export function useIsDarkTheme() {
  const { styleTheme } = useTheme()

  return styleTheme === 'dark'
}

export function useTheme() {
  const context = React.useContext(ThemeContext)

  return context
}

const changeDocumentStyle = (theme: Theme, preferred: Theme) => {
  const target = theme === 'system' ? preferred : theme
  const former = target === 'light' ? 'dark' : 'light'

  document.documentElement.classList.remove(former)
  document.documentElement.classList.add(target)
  document.dispatchEvent(
    new CustomEvent('theme-change', { detail: { theme: target } }),
  )
}

function readLocalTheme() {
  const value = localStorage.getItem(LocalStorageKeys.THEME)

  return value && themes.includes(value as Theme) ? (value as Theme) : 'system'
}

// Should happen before the first render
const initialTheme = readLocalTheme()

function useSelectedTheme() {
  const [theme, setTheme] = useState<Theme>(initialTheme)
  const { isApp } = useDesktopApp()

  const handleSetTheme = useCallback(
    (theme: Theme) => {
      setTheme(theme)
      if (isApp) {
        sendCustomAppAction('set-theme', { theme })
      }
    },
    [isApp],
  )

  useEffect(() => {
    const theme = readLocalTheme()

    handleSetTheme(theme)
  }, [handleSetTheme])

  const change = useCallback(
    (theme: Theme) => {
      localStorage.setItem(LocalStorageKeys.THEME, theme)
      handleSetTheme(theme)
    },
    [handleSetTheme],
  )

  return [theme, change] as const
}

function matchDarkColorPreference() {
  return window.matchMedia('(prefers-color-scheme: dark)')
}

function isPrefersDark() {
  const matcher = matchDarkColorPreference()

  return matcher && matcher.matches
}

const initialPrefersDark = isPrefersDark()
const initialBlackFridayBannerOpen = false

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useSelectedTheme()
  const [prefersDark, setPrefersDark] = useState(initialPrefersDark)
  const [blackFridayBannerOpen, setBlackFridayBannerOpen] = useState(
    initialBlackFridayBannerOpen,
  )

  useEffect(() => {
    const prefersDark = isPrefersDark()

    setPrefersDark(prefersDark)
    const listener = (event: MediaQueryListEvent) =>
      setPrefersDark(event.matches)

    const colorPreferenceMatcher = matchDarkColorPreference()

    if (colorPreferenceMatcher && colorPreferenceMatcher.addEventListener) {
      colorPreferenceMatcher.addEventListener('change', listener)
    }

    return () => {
      if (
        colorPreferenceMatcher &&
        colorPreferenceMatcher.removeEventListener
      ) {
        colorPreferenceMatcher.removeEventListener('change', listener)
      }
    }
  }, [])

  useEffect(() => {
    changeDocumentStyle(theme, prefersDark ? 'dark' : 'light')
  }, [theme, prefersDark])

  useEffect(() => {
    const osClass = getOs().replace(/\s+/g, '-').toLowerCase()

    document.documentElement.classList.add(osClass)
  }, [])

  const context = useMemo(
    () => ({
      theme,
      changeTheme: setTheme,
      styleTheme: theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme,
      blackFridayBannerOpen,
      setBlackFridayBannerOpen,
    }),
    [theme, setTheme, prefersDark, blackFridayBannerOpen],
  )

  return (
    <ThemeContext.Provider value={context}>{children}</ThemeContext.Provider>
  )
}
